<script setup lang="ts">
  import { computed, onBeforeMount, provide, ref, watch } from 'vue';
  import ModalPosViewOrderItems from './ModalPosViewOrderItems.vue';
  import { IOrder } from '@/interfaces/IOrder';
  import ModalPosViewOrderInfo from './ModalPosViewOrderInfo.vue';
  import ModalPosViewOrderStatus from './ModalPosViewOrderStatus.vue';
  import ModalPosViewOrderPaymentStatus from './ModalPosViewOrderPaymentStatus.vue';
  import ModalPosViewOrderActions from './ModalPosViewOrderActions.vue';
  import { useOrdersStore } from '@/stores/orders.store';
  import ModalsCloseButton from '../../ModalsCloseButton.vue';

  const props = defineProps<{
    order: IOrder;
  }>();
  const ordersStore = useOrdersStore();
  const emit = defineEmits(['close']);

  const storeOrder = computed<IOrder | null>(() => {
    const orders = ordersStore.orders;
    if (!orders) return null;
    return orders.find((o) => o.id === props.order.id) ?? null;
  });

  // Provide a store-backed order ref by id (fallback to prop).
  // Important: once we switch to the store instance, keep it even if the store later removes it,
  // so the modal doesn't jump back to a stale prop snapshot.
  const providedOrder = ref<IOrder>(props.order);

  watch(
    storeOrder,
    (o) => {
      if (o) {
        providedOrder.value = o;
      }
    },
    { immediate: true }
  );

  provide('order', providedOrder);

  onBeforeMount(() => {
    if (!storeOrder.value) {
      console.warn('Modal order not found in store, might cause issues');
    }

    if (props.order !== storeOrder.value && storeOrder.value) {
      console.warn(
        'Modal order is different from store order, might cause issues'
      );
    }
  });
</script>

<template>
  <div class="pos-view-order-modal">
    <ModalsCloseButton @close="emit('close')" />
    <div class="modal-inner">
      <div class="modal-body">
        <div class="order-info">
          <ModalPosViewOrderInfo />
          <ModalPosViewOrderStatus />
          <ModalPosViewOrderPaymentStatus />
          <ModalPosViewOrderActions />
        </div>
        <div class="items-summary">
          <ModalPosViewOrderItems />
        </div>
      </div>
      <div class="modal-footer"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-view-order-modal {
    @apply tw-flex tw-w-full tw-relative;

    .modal-inner {
      @apply tw-flex tw-flex-col tw-w-full;

      .modal-header {
        @apply tw-flex-none tw-px-2 tw-py-1;
      }

      .modal-body {
        @apply tw-flex-1 tw-overflow-y-auto;
        @apply tw-flex tw-flex-col tw-space-y-2;
        @apply md:tw-flex-row md:tw-items-stretch;
        @apply tw-divide-x tw-divide-dashed;

        // .order-info and .items 50% each
        .order-info {
          @apply md:tw-flex-1 tw-flex tw-flex-col tw-space-y-2 tw-bg-gray-50 tw-px-2 tw-py-2 tw-rounded-tl-xl;
          @apply md:tw-overflow-y-auto;
        }

        .items-summary {
          @apply md:tw-flex-1 tw-flex tw-flex-col tw-px-2 tw-py-1;
          @apply md:tw-overflow-y-auto;
        }
      }

      .modal-footer {
        @apply tw-flex-none tw-px-2 tw-py-1;
      }
    }
  }
</style>
